import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import api from '@/api';
import { format, addDays, startOfMonth, endOfMonth, startOfWeek, endOfWeek, isSameMonth, isSameDay, addMonths, subMonths, isBefore, startOfDay } from 'date-fns';
import { ru } from 'date-fns/locale';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { PrivacyConsent } from '@/components/PrivacyConsent';
import { CARD_THEMES } from '@/components/cardThemes';
import {
  ArrowRight, Phone, Mail, MapPin, Clock, Shield,
  Users, CalendarDays, MessageCircle, ChevronDown, ChevronLeft,
  Check, FileText, Heart, Star, HelpCircle, X,
} from 'lucide-react';
import PatientChatWidget from '@/components/PatientChatWidget';
import { formatMoney } from '@/lib/currency';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = API.replace('/api', '');

/* ── Scroll animation hook ─────────────────────────────────────────── */
function useScrollReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.12 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

function RevealSection({ children, className = '', delay = 0, ...props }) {
  const [ref, visible] = useScrollReveal();
  return (
    <div ref={ref}
      className={`transition-all duration-700 ease-out ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
      {...props}>
      {children}
    </div>
  );
}

export default function ClinicPage({ slug: propSlug }) {
  const params = useParams();
  const slug = propSlug || params.slug;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Booking state
  const [bookingOpen, setBookingOpen] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [selectedFormat, setSelectedFormat] = useState(null);
  const [bookStep, setBookStep] = useState(0);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calMonth, setCalMonth] = useState(new Date());
  const [selTime, setSelTime] = useState('');
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', email: '' });
  const [submitting, setSubmitting] = useState(false);
  const [bookConsent, setBookConsent] = useState(false);

  // Filter & UI
  const [filterTag, setFilterTag] = useState('');
  const [expandedService, setExpandedService] = useState(null);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [heroOffset, setHeroOffset] = useState(0);
  const [lightbox, setLightbox] = useState(null);

  const teamRef = useRef(null);

  useEffect(() => {
    api.get(`/public/organization/${slug}`)
      .then(({ data: d }) => {
        // If custom landing exists, redirect to it
        if (d.has_custom_landing) {
          window.location.href = `${BASE}/api/org/${slug}/`;
          return;
        }
        setData(d);
        // SEO meta tags
        document.title = d.clinic_name || 'Клиника';
        const metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc) metaDesc.setAttribute('content', d.hero_subtitle || d.about || '');
        else {
          const m = document.createElement('meta');
          m.name = 'description';
          m.content = d.hero_subtitle || d.about || '';
          document.head.appendChild(m);
        }
        // Open Graph
        setOgTag('og:title', d.clinic_name || '');
        setOgTag('og:description', d.hero_subtitle || d.about || '');
        setOgTag('og:type', 'website');
        setOgTag('og:url', window.location.href);
        if (d.logo) setOgTag('og:image', `${BASE}/api/clinic-logos/${d.logo}`);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
    return () => { document.title = 'ProPraxis'; };
  }, [slug]);

  // Parallax effect
  useEffect(() => {
    const handleScroll = () => setHeroOffset(window.scrollY * 0.3);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const theme = data ? (CARD_THEMES[data.theme] || CARD_THEMES.forest) : CARD_THEMES.forest;

  const fetchSlots = useCallback(async (date, docId, fmtId) => {
    setSlotsLoading(true);
    setSlots([]);
    try {
      const { data: s } = await api.get('/public/available-slots', {
        params: { date: format(date, 'yyyy-MM-dd'), format_id: fmtId, psychologist_id: docId },
      });
      setSlots(Array.isArray(s) ? s : []);
    } catch (err) {
      console.error('Slots error:', err);
      setSlots([]);
    }
    setSlotsLoading(false);
  }, []);

  useEffect(() => {
    if (bookStep === 1 && selectedDoctor && selectedFormat && selectedDate) {
      fetchSlots(selectedDate, selectedDoctor.id, selectedFormat.id);
    }
  }, [selectedDate, bookStep, selectedDoctor, selectedFormat, fetchSlots]);

  const startBooking = (doctor) => {
    setSelectedDoctor(doctor);
    setSelectedFormat(null);
    setBookStep(0);
    setSelTime('');
    setSlots([]);
    setSelectedDate(new Date());
    setCalMonth(new Date());
    setForm({ name: '', phone: '', email: '' });
    setBookConsent(false);
    setBookingOpen(true);
  };

  const submitBooking = async () => {
    if (!form.name || !form.phone || !selTime || !selectedFormat || !selectedDoctor) return;
    setSubmitting(true);
    try {
      await api.post('/public/book', {
        patient_name: form.name, patient_phone: form.phone, patient_email: form.email,
        date: format(selectedDate, 'yyyy-MM-dd'), time: selTime,
        format_id: selectedFormat.id, link_id: '', notes: '',
        psychologist_id: selectedDoctor.id,
      });
      setBookStep(3);
    } catch (err) { toast.error(err.response?.data?.detail || 'Ошибка при записи'); }
    setSubmitting(false);
  };

  const scrollToTeam = () => teamRef.current?.scrollIntoView({ behavior: 'smooth' });

  const allTags = data ? [...new Set(data.doctors.flatMap(d => d.tags || []))] : [];
  const filteredDoctors = data ? (filterTag ? data.doctors.filter(d => (d.tags || []).includes(filterTag)) : data.doctors) : [];
  const isSingleDoctor = data?.doctors.length === 1;

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: theme.bg }}>
      <div className="flex flex-col items-center gap-4">
        <div className="w-10 h-10 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: theme.accent, borderTopColor: 'transparent' }} />
        <p className="text-sm" style={{ color: theme.textMuted }}>Загрузка...</p>
      </div>
    </div>
  );

  if (error || !data) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: theme.bg }}>
      <p style={{ color: theme.textMuted }}>Организация не найдена</p>
    </div>
  );

  const logoUrl = data.logo ? `${BASE}/api/clinic-logos/${data.logo}` : '';
  const heroBgUrl = data.hero_bg ? `${BASE}/api/hero-bgs/${data.hero_bg}` : '';

  return (
    <div data-testid="clinic-page" className="min-h-screen" style={{ background: theme.bg, color: theme.text }}>

      {/* ═══ DEMO BANNER ═══ */}
      {data.is_demo && (
        <div data-testid="demo-banner" className="fixed top-0 left-0 right-0 z-50 py-1.5 text-center text-xs font-medium tracking-wide"
          style={{ background: theme.accent, color: '#fff' }}>
          Демо-визитка — все данные вымышлены
        </div>
      )}

      {/* ═══ BLOCK 1: HERO ═══ */}
      <section data-testid="hero-section" className="relative overflow-hidden" style={{ minHeight: '90vh', paddingTop: data.is_demo ? '28px' : 0 }}>
        {/* Parallax background */}
        <div className="absolute inset-0 z-0" style={{ transform: `translateY(${heroOffset}px)` }}>
          {heroBgUrl ? (
            <img src={heroBgUrl} alt="" className="w-full h-full object-cover" style={{ minHeight: '120%' }} />
          ) : (
            <div className="w-full h-full" style={{
              background: `
                radial-gradient(ellipse at 20% 30%, ${theme.accent}12, transparent 50%),
                radial-gradient(ellipse at 80% 70%, ${theme.accent}08, transparent 45%),
                radial-gradient(circle at 50% 50%, ${theme.card}40, transparent 70%),
                ${theme.bg}`,
            }} />
          )}
          <div className="absolute inset-0" style={{
            background: `linear-gradient(180deg, ${theme.bg}95 0%, ${theme.bg}30 35%, ${theme.bg}50 65%, ${theme.bg} 100%)`,
          }} />
        </div>

        {/* Top bar */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {logoUrl ? (
                <img src={logoUrl} alt="" className="w-24 h-24 rounded-2xl object-cover shadow-lg" style={{ border: `1px solid ${theme.border}` }} />
              ) : (
                <div className="w-24 h-24 rounded-2xl flex items-center justify-center shadow-lg"
                  style={{ background: `${theme.accent}20`, border: `1px solid ${theme.border}`, backdropFilter: 'blur(8px)' }}>
                  <Heart size={36} style={{ color: theme.accent }} />
                </div>
              )}
              <span className="font-medium text-sm tracking-wide" style={{ color: theme.textSec }}>
                {data.clinic_name}
              </span>
            </div>
            {data.phone && (
              <a href={`tel:${data.phone}`} className="hidden sm:flex items-center gap-2 text-sm px-5 py-2.5 rounded-xl transition-all hover:scale-[1.02]"
                style={{ color: theme.textSec, background: `${theme.card}90`, border: `1px solid ${theme.border}`, backdropFilter: 'blur(12px)' }}>
                <Phone size={14} /> {data.phone}
              </a>
            )}
          </div>
        </div>

        {/* Hero content */}
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center justify-center text-center"
          style={{ minHeight: 'calc(90vh - 120px)' }}>
          <RevealSection>
            <h1 data-testid="hero-title" className="font-heading text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight mb-6"
              style={{ color: theme.text }}>
              {data.hero_title || data.clinic_name}
            </h1>
          </RevealSection>
          {data.hero_subtitle && (
            <RevealSection delay={150}>
              <p data-testid="hero-subtitle" className="text-base sm:text-lg max-w-2xl leading-relaxed mb-10" style={{ color: theme.textSec }}>
                {data.hero_subtitle}
              </p>
            </RevealSection>
          )}
          <RevealSection delay={300}>
            <Button data-testid="hero-cta" onClick={scrollToTeam}
              className="h-13 px-10 text-base font-medium text-white shadow-xl transition-all hover:scale-[1.03] hover:shadow-2xl active:scale-[0.97]"
              style={{ background: theme.accent, boxShadow: `0 8px 30px ${theme.accent}40` }}>
              Записаться на приём <ArrowRight size={18} className="ml-2" />
            </Button>
          </RevealSection>
        </div>

        {/* Scroll hint */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
          <div className="w-6 h-10 rounded-full flex items-start justify-center pt-2" style={{ border: `2px solid ${theme.textMuted}40` }}>
            <div className="w-1.5 h-3 rounded-full animate-bounce" style={{ background: theme.textMuted }} />
          </div>
        </div>
      </section>

      {/* ═══ BLOCK 2: ABOUT & LICENSES ═══ */}
      {(data.about || data.license_number) && (
        <section data-testid="about-section" className="py-16 md:py-24" style={{ background: theme.card }}>
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-16 items-start">
              <RevealSection className="md:col-span-2">
                <h2 className="font-heading text-xl sm:text-2xl mb-6" style={{ color: theme.text }}>О клинике</h2>
                <p className="text-sm sm:text-base leading-relaxed whitespace-pre-line" style={{ color: theme.textSec }}>
                  {data.about}
                </p>
              </RevealSection>

              {data.license_number && (
                <RevealSection delay={200}>
                  <div className="p-6 rounded-2xl relative overflow-hidden" style={{ background: theme.bg, border: `1px solid ${theme.border}` }}>
                    <div className="absolute top-0 right-0 w-20 h-20 rounded-bl-full" style={{ background: `${theme.accent}08` }} />
                    <div className="flex items-center gap-2 mb-4">
                      <Shield size={20} style={{ color: theme.accent }} />
                      <span className="text-xs font-bold uppercase tracking-widest" style={{ color: theme.accent }}>
                        Лицензия
                      </span>
                    </div>
                    <p className="text-sm font-medium leading-snug" style={{ color: theme.text }}>
                      {data.license_number}
                    </p>
                    {data.license_date && (
                      <p className="text-xs mt-2" style={{ color: theme.textMuted }}>от {data.license_date}</p>
                    )}
                  </div>
                </RevealSection>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ═══ BLOCK 3: SPECIALISTS ═══ */}
      <section ref={teamRef} data-testid="team-section" className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <RevealSection>
            <h2 className="font-heading text-xl sm:text-2xl mb-3 text-center" style={{ color: theme.text }}>
              {isSingleDoctor ? 'Специалист' : 'Наши специалисты'}
            </h2>
            <p className="text-sm text-center mb-10" style={{ color: theme.textMuted }}>
              {isSingleDoctor ? 'Ваш врач' : 'Выберите специалиста и запишитесь на приём'}
            </p>
          </RevealSection>

          {/* Tag filter */}
          {allTags.length > 0 && !isSingleDoctor && (
            <RevealSection delay={100}>
              <div data-testid="tag-filter" className="flex flex-wrap justify-center gap-2 mb-10">
                <button onClick={() => setFilterTag('')}
                  className="px-4 py-1.5 rounded-full text-xs font-medium transition-all"
                  style={{
                    background: !filterTag ? theme.accent : theme.card,
                    color: !filterTag ? '#fff' : theme.textSec,
                    border: `1px solid ${!filterTag ? theme.accent : theme.border}`,
                  }}>
                  Все
                </button>
                {allTags.map(tag => (
                  <button key={tag} onClick={() => setFilterTag(tag === filterTag ? '' : tag)}
                    className="px-4 py-1.5 rounded-full text-xs font-medium transition-all"
                    style={{
                      background: filterTag === tag ? theme.accent : theme.card,
                      color: filterTag === tag ? '#fff' : theme.textSec,
                      border: `1px solid ${filterTag === tag ? theme.accent : theme.border}`,
                    }}>
                    {tag}
                  </button>
                ))}
              </div>
            </RevealSection>
          )}

          {/* Doctor cards */}
          {isSingleDoctor ? (
            <RevealSection delay={200}>
              <SingleDoctorCard doctor={data.doctors[0]} theme={theme} onBook={() => startBooking(data.doctors[0])} />
            </RevealSection>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredDoctors.map((doc, i) => (
                <RevealSection key={doc.id} delay={i * 100}>
                  <DoctorCard doctor={doc} theme={theme} onBook={() => startBooking(doc)} />
                </RevealSection>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ═══ BLOCK 4: SERVICES & PRICES ═══ */}
      {data.all_formats.length > 0 && (
        <section data-testid="services-section" className="py-16 md:py-24" style={{ background: theme.card }}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <RevealSection>
              <h2 className="font-heading text-xl sm:text-2xl mb-3 text-center" style={{ color: theme.text }}>
                Услуги и цены
              </h2>
              <p className="text-sm text-center mb-10" style={{ color: theme.textMuted }}>
                Полный список доступных услуг
              </p>
            </RevealSection>

            <div className="space-y-3">
              {data.all_formats.map((fmt, i) => (
                <RevealSection key={`${fmt.id}-${i}`} delay={i * 60}>
                  <div className="rounded-2xl overflow-hidden transition-all"
                    style={{ background: theme.bg, border: `1px solid ${theme.border}` }}>
                    <button
                      data-testid={`service-${fmt.id}`}
                      onClick={() => setExpandedService(expandedService === i ? null : i)}
                      className="w-full flex items-center justify-between p-5 text-left transition-colors"
                      style={{ color: theme.text }}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{fmt.name}</p>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-xs flex items-center gap-1" style={{ color: theme.textMuted }}>
                            <Clock size={11} /> {fmt.duration} мин.
                          </span>
                          {fmt.doctor_name && (
                            <span className="text-xs" style={{ color: theme.textMuted }}>{fmt.doctor_name}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-4 ml-4">
                        {fmt.price > 0 && (
                          <span className="text-sm font-semibold whitespace-nowrap" style={{ color: theme.accent }}>
                            {formatMoney(fmt.price, fmt.currency || data.currency)}
                          </span>
                        )}
                        {fmt.price === 0 && (
                          <Badge className="text-[10px]" style={{ background: `${theme.accent}15`, color: theme.accent, border: 'none' }}>
                            Бесплатно
                          </Badge>
                        )}
                        <ChevronDown size={16} className={`transition-transform duration-200 ${expandedService === i ? 'rotate-180' : ''}`}
                          style={{ color: theme.textMuted }} />
                      </div>
                    </button>
                    <div className={`transition-all duration-300 overflow-hidden ${expandedService === i ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'}`}>
                      <div className="px-5 pb-5">
                        {fmt.description && (
                          <p className="text-xs leading-relaxed mb-4" style={{ color: theme.textSec }}>{fmt.description}</p>
                        )}
                        {fmt.doctor_id && (
                          <Button size="sm" onClick={() => {
                            const doc = data.doctors.find(d => d.id === fmt.doctor_id);
                            if (doc) { startBooking(doc); setSelectedFormat(fmt); setBookStep(1); }
                          }}
                            className="text-white text-xs" style={{ background: theme.accent }}>
                            Записаться <ArrowRight size={14} className="ml-1" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </RevealSection>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ═══ BLOCK 5: HOW WE WORK ═══ */}
      <section data-testid="journey-section" className="py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <RevealSection>
            <h2 className="font-heading text-xl sm:text-2xl mb-3 text-center" style={{ color: theme.text }}>
              Как мы работаем
            </h2>
            <p className="text-sm text-center mb-14" style={{ color: theme.textMuted }}>
              Три простых шага до вашего первого приёма
            </p>
          </RevealSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {[
              { icon: <CalendarDays size={28} />, title: 'Запись', desc: 'Выберите удобное время, услугу и специалиста прямо на этом сайте.' },
              { icon: <FileText size={28} />, title: 'Личный кабинет', desc: 'Получите ссылку в ваш защищённый портал с полной информацией о записи.' },
              { icon: <MessageCircle size={28} />, title: 'Поддержка 24/7', desc: 'Общайтесь с врачом и администратором в конфиденциальном чате без сторонних мессенджеров.' },
            ].map((step, i) => (
              <RevealSection key={i} delay={i * 150}>
                <div className="text-center group">
                  <div className="w-16 h-16 rounded-2xl mx-auto mb-5 flex items-center justify-center transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3"
                    style={{ background: `${theme.accent}15`, color: theme.accent, boxShadow: `0 4px 20px ${theme.accent}15` }}>
                    {step.icon}
                  </div>
                  <div className="flex items-center justify-center gap-2 mb-3">
                    <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold"
                      style={{ background: theme.accent, color: '#fff' }}>
                      {i + 1}
                    </span>
                    <h3 className="text-sm font-semibold" style={{ color: theme.text }}>{step.title}</h3>
                  </div>
                  <p className="text-xs leading-relaxed max-w-xs mx-auto" style={{ color: theme.textSec }}>
                    {step.desc}
                  </p>
                </div>
              </RevealSection>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ CERTIFICATES / DIPLOMAS ═══ */}
      {data.certificates?.length > 0 && (
        <section data-testid="certificates-section" className="py-16 md:py-24" style={{ background: theme.card }}>
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <RevealSection>
              <h2 className="font-heading text-xl sm:text-2xl mb-3 text-center" style={{ color: theme.text }}>
                Дипломы и сертификаты
              </h2>
              <p className="text-sm text-center mb-10" style={{ color: theme.textMuted }}>Подтверждённая квалификация</p>
            </RevealSection>
            <RevealSection delay={150}>
              <div className="flex gap-3 overflow-x-auto pb-2 justify-center flex-wrap">
                {data.certificates.map((c, i) => (
                  <div key={i} className="shrink-0 w-36 h-36 rounded-xl overflow-hidden cursor-pointer hover:opacity-80 transition-opacity"
                    style={{ border: `1px solid ${theme.border}` }}
                    onClick={() => setLightbox(i)}>
                    <img src={`${BASE}/api/clinic-certs/${c}`} alt="" className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            </RevealSection>
          </div>
        </section>
      )}

      {/* ═══ REVIEWS ═══ */}
      {data.reviews?.length > 0 && (
        <section data-testid="reviews-section" className="py-16 md:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <RevealSection>
              <h2 className="font-heading text-xl sm:text-2xl mb-3 text-center" style={{ color: theme.text }}>
                Отзывы пациентов
              </h2>
              <p className="text-sm text-center mb-10" style={{ color: theme.textMuted }}>Что говорят наши пациенты</p>
            </RevealSection>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {data.reviews.map((r, i) => (
                <RevealSection key={i} delay={i * 100}>
                  <div className="p-5 rounded-2xl" style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
                    <div className="flex items-center gap-1 mb-3">
                      {[1,2,3,4,5].map(s => (
                        <Star key={s} size={14} style={{ color: s <= (r.rating || 5) ? theme.accent : theme.border }} fill={s <= (r.rating || 5) ? theme.accent : 'none'} />
                      ))}
                    </div>
                    <p className="text-sm leading-relaxed mb-3" style={{ color: theme.textSec }}>"{r.text}"</p>
                    <p className="text-xs font-medium" style={{ color: theme.textMuted }}>— {r.author || 'Аноним'}</p>
                  </div>
                </RevealSection>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ═══ FAQ ═══ */}
      {data.faq?.length > 0 && (
        <section data-testid="faq-section" className="py-16 md:py-24" style={{ background: data.reviews?.length > 0 ? theme.card : undefined }}>
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <RevealSection>
              <h2 className="font-heading text-xl sm:text-2xl mb-3 text-center" style={{ color: theme.text }}>
                Частые вопросы
              </h2>
              <p className="text-sm text-center mb-10" style={{ color: theme.textMuted }}>Ответы на популярные вопросы</p>
            </RevealSection>
            <div className="space-y-2">
              {data.faq.map((item, i) => (
                <RevealSection key={i} delay={i * 60}>
                  <div className="rounded-2xl overflow-hidden" style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
                    <button onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                      className="w-full flex items-center justify-between p-5 text-left text-sm" style={{ color: theme.text }}>
                      {item.q}
                      <ChevronDown size={16} className={`shrink-0 ml-2 transition-transform duration-200 ${expandedFaq === i ? 'rotate-180' : ''}`}
                        style={{ color: theme.textMuted }} />
                    </button>
                    <div className={`transition-all duration-300 overflow-hidden ${expandedFaq === i ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'}`}>
                      <p className="px-5 pb-5 text-xs leading-relaxed" style={{ color: theme.textSec }}>{item.a}</p>
                    </div>
                  </div>
                </RevealSection>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ═══ BLOCK 6: FOOTER ═══ */}
      <footer data-testid="footer-section" className="py-12 md:py-16" style={{ background: theme.card, borderTop: `1px solid ${theme.border}` }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {/* Map or address */}
            <div className="md:col-span-2">
              {data.map_embed ? (
                <div className="rounded-2xl overflow-hidden mb-4" style={{ border: `1px solid ${theme.border}` }}>
                  <iframe src={data.map_embed} width="100%" height="250" frameBorder="0" allowFullScreen style={{ display: 'block' }} title="Карта" />
                </div>
              ) : data.address ? (
                <div className="p-6 rounded-2xl mb-4" style={{ background: theme.bg, border: `1px solid ${theme.border}` }}>
                  <div className="flex items-start gap-3">
                    <MapPin size={18} style={{ color: theme.accent }} className="mt-0.5 shrink-0" />
                    <p className="text-sm" style={{ color: theme.textSec }}>{data.address}</p>
                  </div>
                </div>
              ) : null}

              <div className="flex flex-wrap gap-3">
                {data.phone && (
                  <a href={`tel:${data.phone}`} className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm transition-all hover:scale-[1.02]"
                    style={{ background: theme.bg, color: theme.textSec, border: `1px solid ${theme.border}` }}>
                    <Phone size={14} style={{ color: theme.accent }} /> {data.phone}
                  </a>
                )}
                {data.email && (
                  <a href={`mailto:${data.email}`} className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm transition-all hover:scale-[1.02]"
                    style={{ background: theme.bg, color: theme.textSec, border: `1px solid ${theme.border}` }}>
                    <Mail size={14} style={{ color: theme.accent }} /> {data.email}
                  </a>
                )}
              </div>
            </div>

            {/* Info column */}
            <div className="space-y-5">
              <div className="flex items-center gap-3">
                {logoUrl ? (
                  <img src={logoUrl} alt="" className="w-9 h-9 rounded-xl object-cover" />
                ) : (
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${theme.accent}20` }}>
                    <Heart size={14} style={{ color: theme.accent }} />
                  </div>
                )}
                <span className="text-sm font-medium" style={{ color: theme.text }}>{data.clinic_name}</span>
              </div>

              {data.working_hours_text && (
                <div className="flex items-start gap-2">
                  <Clock size={14} style={{ color: theme.accent }} className="mt-0.5 shrink-0" />
                  <p className="text-xs whitespace-pre-line" style={{ color: theme.textSec }}>{data.working_hours_text}</p>
                </div>
              )}

              <div className="space-y-1 pt-3" style={{ borderTop: `1px solid ${theme.border}` }}>
                {data.inn && <p className="text-xs" style={{ color: theme.textMuted }}>ИНН: {data.inn}</p>}
                {data.ogrn && <p className="text-xs" style={{ color: theme.textMuted }}>ОГРН: {data.ogrn}</p>}
                <div className="flex flex-col gap-1 pt-2">
                  {data.privacy_url && (
                    <a href={data.privacy_url} target="_blank" rel="noopener noreferrer"
                      className="text-xs underline underline-offset-2" style={{ color: theme.textMuted }}>
                      Политика конфиденциальности
                    </a>
                  )}
                  {data.offer_url && (
                    <a href={data.offer_url} target="_blank" rel="noopener noreferrer"
                      className="text-xs underline underline-offset-2" style={{ color: theme.textMuted }}>
                      Договор-оферта
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-10 pt-6 text-center" style={{ borderTop: `1px solid ${theme.border}` }}>
            <a href="/" className="inline-flex items-center gap-2 text-xs transition-colors hover:opacity-80" style={{ color: theme.textMuted }}>
              Сделано в <span style={{ color: theme.accent }}>ProPraxis</span>
            </a>
          </div>
        </div>
      </footer>

      {/* ═══ BOOKING MODAL (custom overlay, no Dialog) ═══ */}
      {bookingOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setBookingOpen(false)}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div
            onClick={e => e.stopPropagation()}
            className="relative z-10 w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden"
            style={{ background: theme.bg, border: `1px solid ${theme.border}`, color: theme.text }}>

            {/* Header */}
            <div className="flex items-center justify-between px-5 pt-5 pb-3">
              <h2 className="text-base font-semibold" style={{ color: theme.text }}>
                {bookStep === 3 ? 'Готово' : `Запись к ${selectedDoctor?.name || 'специалисту'}`}
              </h2>
              <button onClick={() => setBookingOpen(false)} className="w-7 h-7 rounded-full flex items-center justify-center transition-colors hover:opacity-70"
                style={{ background: theme.card }}>
                <X size={14} style={{ color: theme.textMuted }} />
              </button>
            </div>

            {/* Body */}
            <div className="px-5 pb-5 max-h-[70vh] overflow-y-auto">

              {/* Step 0: Format selection */}
              {bookStep === 0 && selectedDoctor && (
                <div data-testid="booking-format-step" className="space-y-2 pt-1">
                  <p className="text-[11px] mb-1" style={{ color: theme.textMuted }}>Выберите услугу:</p>
                  {(selectedDoctor.formats || []).map(f => (
                    <button key={f.id} onClick={() => { setSelectedFormat(f); setBookStep(1); }}
                      data-testid={`book-format-${f.id}`}
                      className="w-full text-left p-3.5 rounded-xl transition-all hover:scale-[1.01] active:scale-[0.99]"
                      style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
                      <div className="text-sm font-medium" style={{ color: theme.text }}>{f.name}</div>
                      <div className="text-[11px] mt-0.5" style={{ color: theme.textMuted }}>
                        {f.duration} мин.{f.price ? ` · ${formatMoney(f.price, f.currency || data.currency)}` : ' · бесплатно'}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Step 1: Calendar + time slots (two-column) */}
              {bookStep === 1 && (() => {
                const today = startOfDay(new Date());
                const monthStart = startOfMonth(calMonth);
                const monthEnd = endOfMonth(calMonth);
                const calStart = startOfWeek(monthStart, { weekStartsOn: 1 });
                const calEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
                const calDays = [];
                let d = calStart;
                while (d <= calEnd) { calDays.push(d); d = addDays(d, 1); }
                const dayLabels = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

                return (
                  <div data-testid="booking-datetime-step" className="pt-1">
                    <div className="flex items-center justify-between mb-3">
                      <button onClick={() => { setBookStep(0); setSelTime(''); }} className="text-[11px] flex items-center gap-0.5" style={{ color: theme.textMuted }}>
                        <ChevronLeft size={12} /> Назад
                      </button>
                      <span className="text-[11px] font-medium" style={{ color: theme.accent }}>{selectedFormat?.name}</span>
                    </div>

                    <div className="flex gap-4">
                      {/* Left: Calendar grid */}
                      <div className="shrink-0" style={{ width: 260 }}>
                        <div className="rounded-xl p-3" style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
                          {/* Month nav */}
                          <div className="flex items-center justify-between mb-3">
                            <button onClick={() => setCalMonth(subMonths(calMonth, 1))}
                              className="w-7 h-7 rounded-lg flex items-center justify-center transition-colors hover:opacity-70"
                              style={{ color: theme.textMuted }}
                              disabled={isSameMonth(calMonth, new Date()) || isBefore(subMonths(calMonth, 1), startOfMonth(new Date()))}>
                              <ChevronLeft size={16} />
                            </button>
                            <span className="text-sm font-semibold capitalize" style={{ color: theme.text }}>
                              {format(calMonth, 'LLLL yyyy', { locale: ru })}
                            </span>
                            <button onClick={() => setCalMonth(addMonths(calMonth, 1))}
                              className="w-7 h-7 rounded-lg flex items-center justify-center transition-colors hover:opacity-70"
                              style={{ color: theme.textMuted }}>
                              <ChevronLeft size={16} className="rotate-180" />
                            </button>
                          </div>
                          {/* Day labels */}
                          <div className="grid grid-cols-7 mb-1">
                            {dayLabels.map((dl, i) => (
                              <div key={dl} className="text-center text-[10px] font-medium py-1"
                                style={{ color: i >= 5 ? '#ef4444' : theme.accent }}>{dl}</div>
                            ))}
                          </div>
                          {/* Day cells */}
                          <div className="grid grid-cols-7">
                            {calDays.map(day => {
                              const inMonth = isSameMonth(day, calMonth);
                              const isPast = isBefore(day, today);
                              const isSelected = isSameDay(day, selectedDate);
                              const disabled = !inMonth || isPast;
                              return (
                                <button key={day.toISOString()}
                                  disabled={disabled}
                                  onClick={() => { setSelectedDate(day); setSelTime(''); }}
                                  className="w-full aspect-square flex items-center justify-center text-xs rounded-full transition-all"
                                  style={{
                                    background: isSelected ? theme.accent : 'transparent',
                                    color: isSelected ? '#fff' : disabled ? `${theme.textMuted}40` : isSameDay(day, today) ? theme.accent : theme.text,
                                    fontWeight: isSelected || isSameDay(day, today) ? 700 : 400,
                                    cursor: disabled ? 'default' : 'pointer',
                                  }}>
                                  {format(day, 'd')}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </div>

                      {/* Right: Time slots */}
                      <div className="flex-1 min-w-0">
                        <div className="rounded-xl p-3 h-full" style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
                          <p className="text-[10px] font-medium mb-2" style={{ color: theme.textMuted }}>
                            {format(selectedDate, 'd MMMM, EEEE', { locale: ru })}
                          </p>
                          {slotsLoading ? (
                            <div className="flex items-center justify-center gap-2 py-10">
                              <div className="w-4 h-4 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: theme.accent, borderTopColor: 'transparent' }} />
                            </div>
                          ) : slots.length === 0 ? (
                            <p className="text-[11px] text-center py-10" style={{ color: theme.textMuted }}>Нет свободного времени</p>
                          ) : (
                            <div className="grid grid-cols-3 gap-1.5">
                              {slots.map(t => (
                                <button key={t} onClick={() => { setSelTime(t); setBookStep(2); }}
                                  data-testid={`slot-${t}`}
                                  className="py-2.5 rounded-lg text-xs font-medium transition-all text-center"
                                  style={selTime === t
                                    ? { background: theme.accent, color: '#fff' }
                                    : { background: theme.bg, color: theme.textSec, border: `1px solid ${theme.border}` }}>
                                  {t}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Step 2: Contact form */}
              {bookStep === 2 && (
                <div data-testid="booking-contact-step" className="space-y-3 pt-1">
                  <button onClick={() => setBookStep(1)} className="text-[11px] flex items-center gap-0.5" style={{ color: theme.textMuted }}>
                    <ChevronLeft size={12} /> Назад
                  </button>
                  <div className="p-3 rounded-xl text-center" style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
                    <div className="text-[11px] font-medium" style={{ color: theme.accent }}>{selectedFormat?.name}</div>
                    <div className="text-sm font-semibold mt-0.5" style={{ color: theme.text }}>
                      {format(selectedDate, 'd MMMM', { locale: ru })} в {selTime}
                    </div>
                    <div className="text-[10px] mt-0.5" style={{ color: theme.textMuted }}>{selectedDoctor?.name}</div>
                  </div>
                  <Input data-testid="booking-name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="Ваше имя *" className="h-10 text-sm" style={{ background: theme.card, borderColor: theme.border, color: theme.text }} />
                  <Input data-testid="booking-phone" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                    placeholder="Телефон *" className="h-10 text-sm" style={{ background: theme.card, borderColor: theme.border, color: theme.text }} />
                  <Input data-testid="booking-email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    placeholder="Email (необязательно)" className="h-10 text-sm" style={{ background: theme.card, borderColor: theme.border, color: theme.text }} />
                  <PrivacyConsent checked={bookConsent} onChange={setBookConsent} />
                  <button data-testid="submit-clinic-booking" onClick={submitBooking}
                    disabled={submitting || !form.name || !form.phone || !bookConsent}
                    className="w-full py-2.5 rounded-xl text-sm font-medium text-white transition-all disabled:opacity-40"
                    style={{ background: theme.accent }}>
                    {submitting ? 'Записываем...' : 'Записаться'}
                  </button>
                </div>
              )}

              {/* Step 3: Success */}
              {bookStep === 3 && (
                <div data-testid="booking-success-step" className="text-center py-6">
                  <div className="w-14 h-14 rounded-full mx-auto mb-3 flex items-center justify-center"
                    style={{ background: `${theme.accent}20` }}>
                    <Check size={28} style={{ color: theme.accent }} />
                  </div>
                  <h3 className="font-semibold text-base mb-1" style={{ color: theme.text }}>Записано!</h3>
                  <p className="text-xs mb-5" style={{ color: theme.textMuted }}>
                    Ожидайте подтверждения от {selectedDoctor?.name || 'специалиста'}
                  </p>
                  <button onClick={() => setBookingOpen(false)}
                    className="px-6 py-2 rounded-xl text-sm font-medium text-white transition-all"
                    style={{ background: theme.accent }}>
                    Закрыть
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ═══ CERTIFICATE LIGHTBOX ═══ */}
      {lightbox !== null && data.certificates && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={() => setLightbox(null)} style={{ background: 'rgba(0,0,0,0.9)' }}>
          <button className="absolute top-4 right-4 text-white/60 hover:text-white" onClick={() => setLightbox(null)}><X size={24} /></button>
          <img src={`${BASE}/api/clinic-certs/${data.certificates[lightbox]}`} alt="" className="max-w-full max-h-[85vh] rounded-lg" onClick={e => e.stopPropagation()} />
        </div>
      )}

      {/* ═══ CHAT WIDGET ═══ */}
      {data.owner_id && (
        <PatientChatWidget ownerId={data.owner_id} patientName="" patientPhone="" isClinic />
      )}
    </div>
  );
}

/* ── Helpers ──────────────────────────────────────────────────────── */
function setOgTag(property, content) {
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) { el = document.createElement('meta'); el.setAttribute('property', property); document.head.appendChild(el); }
  el.setAttribute('content', content);
}

/* ── Doctor card (grid) ──────────────────────────────────────────── */
function DoctorCard({ doctor, theme, onBook }) {
  const avatar = doctor.avatar ? `${BASE}/api/avatars/${doctor.avatar}` : '';
  return (
    <div data-testid={`doctor-card-${doctor.id}`}
      className="rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-xl group"
      style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
      <div className="aspect-[4/3] flex items-center justify-center overflow-hidden" style={{ background: theme.bg }}>
        {avatar ? (
          <img src={avatar} alt={doctor.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" style={{ objectPosition: 'center 15%' }} />
        ) : (
          <div className="w-20 h-20 rounded-full flex items-center justify-center" style={{ background: `${theme.accent}18` }}>
            <span className="text-3xl font-heading" style={{ color: theme.accent }}>{(doctor.name || '?')[0]}</span>
          </div>
        )}
      </div>
      <div className="p-5">
        <h3 className="text-sm font-semibold mb-1" style={{ color: theme.text }}>{doctor.name}</h3>
        {doctor.specialization && <p className="text-xs mb-3" style={{ color: theme.textMuted }}>{doctor.specialization}</p>}
        {doctor.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-4">
            {doctor.tags.slice(0, 3).map((t, i) => (
              <Badge key={i} className="text-[10px] px-2 py-0.5" style={{ background: `${theme.accent}12`, color: theme.accent, border: 'none' }}>{t}</Badge>
            ))}
          </div>
        )}
        <div className="flex flex-col gap-2">
          <Button data-testid={`book-doctor-${doctor.id}`}
            onClick={onBook}
            size="sm" className="w-full text-white text-xs transition-all hover:shadow-lg"
            style={{ background: theme.accent }}>
            Записаться <ArrowRight size={14} className="ml-1" />
          </Button>
          {doctor.slug && (
            <a href={`/p/${doctor.slug}`} target="_blank" rel="noopener noreferrer"
              className="text-[10px] text-center transition-colors hover:underline" style={{ color: theme.textMuted }}>
              Подробнее о специалисте
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Single doctor (large) ───────────────────────────────────────── */
function SingleDoctorCard({ doctor, theme, onBook }) {
  const avatar = doctor.avatar ? `${BASE}/api/avatars/${doctor.avatar}` : '';
  return (
    <div data-testid={`doctor-card-${doctor.id}`}
      className="max-w-2xl mx-auto rounded-2xl overflow-hidden"
      style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
      <div className="flex flex-col sm:flex-row">
        <div className="sm:w-72 h-72 sm:h-auto flex items-center justify-center overflow-hidden shrink-0" style={{ background: theme.bg }}>
          {avatar ? (
            <img src={avatar} alt={doctor.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-28 h-28 rounded-full flex items-center justify-center" style={{ background: `${theme.accent}18` }}>
              <span className="text-5xl font-heading" style={{ color: theme.accent }}>{(doctor.name || '?')[0]}</span>
            </div>
          )}
        </div>
        <div className="p-6 sm:p-8 flex flex-col justify-center flex-1">
          <h3 className="font-heading text-lg sm:text-xl mb-1" style={{ color: theme.text }}>{doctor.name}</h3>
          {doctor.specialization && <p className="text-sm mb-3" style={{ color: theme.textSec }}>{doctor.specialization}</p>}
          {doctor.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-4">
              {doctor.tags.map((t, i) => (
                <Badge key={i} className="text-xs px-2.5 py-0.5" style={{ background: `${theme.accent}12`, color: theme.accent, border: 'none' }}>{t}</Badge>
              ))}
            </div>
          )}
          {doctor.bio && <p className="text-xs leading-relaxed mb-5" style={{ color: theme.textMuted }}>{doctor.bio}</p>}
          <div className="flex flex-col sm:flex-row gap-2">
            <Button data-testid={`book-doctor-${doctor.id}`}
              onClick={onBook}
              className="w-full sm:w-auto text-white" style={{ background: theme.accent }}>
              Записаться <ArrowRight size={16} className="ml-2" />
            </Button>
            {doctor.slug && (
              <a href={`/p/${doctor.slug}`} target="_blank" rel="noopener noreferrer"
                className="text-xs text-center sm:text-left py-2 transition-colors hover:underline" style={{ color: theme.textMuted }}>
                Подробнее
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
